import React, { useState, useRef, useEffect } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputMask } from "primereact/inputmask";
import { Password } from "primereact/password";
import { Checkbox } from "primereact/checkbox";
import { Messages } from "primereact/messages";
import equipeService from "../services/equipeService";
import { hasAnyAuthority, hideProcessing, showProcessing } from "../utils/utils";
import { Dropdown } from "primereact/dropdown";
import { Paginator } from "primereact/paginator";
import { ListBox } from "primereact/listbox";
import { InputNumber } from "primereact/inputnumber";
import { Toast } from "primereact/toast";
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import Agenda from "./Agenda";
import agendaService from "../services/agendaService";

let _equipesInitialized = false;

export default function Equipes() {
    const [equipes, setEquipes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [displayDialog, setDisplayDialog] = useState(false);
    const [equipe, setEquipe] = useState({});
    const msgRef = useRef();
    const [agendamentos, setAgendamentos] = useState(null);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    })

    // Buscar equipes ao montar o componente
    useEffect(() => {

        if (!_equipesInitialized) {
            _equipesInitialized = true;
            return;
        }

        search();
        
    }, []);

    const search = async () => {
        setLoading(true);
        showProcessing();

        try {
            const response = await equipeService.getAll();

            if (response.status === 200) {
                setEquipes(response.data.content || response.data);
            }
            
            hideProcessing();
            setLoading(false);
            
        } catch (e) {
            hideProcessing();
            setLoading(false);
            
            msgRef.current.show({
                closable: false,
                severity: "error",
                summary: "Erro",
                detail: e.response?.data?.message || "Erro ao buscar equipes",
            });
        }
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };

        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const statusBodyTemplate = (rowData) => {
        const severity = rowData.ativo ? "success" : "danger";
        const label = rowData.ativo ? "Ativo" : "Inativo";
        return <span className={`p-badge p-badge-${severity}`}>{label}</span>;
    };

    const tipoServicoBodyTemplate = (rowData) => {
        const tipos = JSON.parse(rowData.tipoServico || "[]");
        return <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
            {tipos.map((tipo, index) => (
            <span key={index} className="tipo-servico-badge">
                <span className={`p-badge p-badge-info`}>{tipo}</span>
            </span>
            ))}
        </div>
        
    }

    const deleteEquipe = async (rowData) => {
        if (window.confirm("Tem certeza que deseja deletar esta equipe?")) {
            try {
                const response = await equipeService.delete(rowData.id);
                if (response.status === 200) {
                    msgRef.current.show({
                        closable: false,
                        severity: "success",
                        summary: "Sucesso",
                        detail: "Equipe deletada com sucesso",
                    });
                    search();
                } else {
                    msgRef.current.show({
                        closable: false,
                        severity: "error",
                        summary: "Erro",
                        detail: "Erro ao deletar equipe",
                    });
                }
            } catch (e) {
                msgRef.current.show({
                    closable: false,
                    severity: "error",
                    summary: "Erro",
                    detail: e.response?.data?.message || "Erro ao deletar equipe",
                });
            }
        }
    }

    const searchAgendamentos = async (idEquipe) => {
        try {
            await setLoading(true);
            await showProcessing();

            const response = await agendaService.getByEquipe(idEquipe);
            if (response.status === 200) {
                setAgendamentos(response.data);
            } else {
                msgRef.current.show({
                    closable: false,
                    severity: "error",
                    summary: "Erro",
                    detail: "Erro ao buscar agendamentos",
                });
            }
        } catch (e) {
            msgRef.current.show({
                closable: false,
                severity: "error",
                summary: "Erro",
                detail: e.response?.data?.message || "Erro ao buscar agendamentos",
            });
        }
        hideProcessing();
        setLoading(false);
    }

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="action-buttons">
                <Button 
                    icon="pi pi-pencil" 
                    className="button-data-table"
                    style={{ backgroundColor: "var(--app-orange-color)" }}
                    tooltip="Editar"
                    onClick={() => {setEquipe(rowData); setDisplayDialog(true);}}
                />
                <Button 
                    icon="pi pi-trash" 
                    className="button-data-table"
                    style={{ backgroundColor: "var(--app-red-color)" }}
                    tooltip="Deletar"
                    onClick={() => deleteEquipe(rowData)}
                />
                <Button 
                    icon="pi pi-calendar" 
                    className="button-data-table"
                    style={{ backgroundColor: "var(--app-blue-color)" }}
                    tooltip="Agenda"
                    onClick={() => searchAgendamentos(rowData.id)}
                />
            </div>
        );
    };

    const save = async () => {
        if (!equipe.nome || !equipe.integrantes || !equipe.tipoServico || JSON.parse(equipe.tipoServico).length === 0) {
            msgRef.current.show({
                closable: false,
                severity: "warn",
                summary: "Aviso",
                detail: "Preencha todos os campos obrigatórios",
            });
            return;
        }

        try {
            const response = await equipeService.save(equipe);

            if (response.status === 200) {
                msgRef.current.show({
                    closable: false,
                    severity: "success",
                    summary: "Sucesso",
                    detail: "Equipe salvo com sucesso",
                });
                setDisplayDialog(false);
                search();
            } else {
                msgRef.current.show({
                    closable: false,
                    severity: "error",
                    summary: "Erro",
                    detail: "Erro ao salvar equipe",
                });
            }
        } catch (e) {
            msgRef.current.show({
                closable: false,
                severity: "error",
                summary: "Erro",
                detail: e.response?.data?.message || "Erro ao salvar equipe",
            });
        }
    };

    const renderHeader = () => {
        return (
            <div className="flex justify-content-end">
                <IconField iconPosition="left">
                    <InputIcon className="pi pi-search" />
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Procurar..." />
                </IconField>
            </div>
        );
    };

    return (
        <div className="page-container">
            <Toast ref={msgRef} className="login-messages" />
            {agendamentos &&
                <Dialog visible={true} style={{ width: "80vw" }} header="Agenda" modal className="dialog" onHide={() => setAgendamentos(null)}>
                    <Agenda agendamentos={agendamentos} />
                </Dialog>
            }
            
            
            <Card className="page-card">
                <div className="page-header">
                    <h2>Gerenciamento de Equipes</h2>
                    <Button 
                        label="Nova Equipe" 
                        icon="pi pi-plus"
                        onClick={() => {
                            setEquipe({terceirizada: false, ativo: true, tipoServico: JSON.stringify([])});
                            setDisplayDialog(true);
                        }}
                        className="button-new"
                    />
                </div>

                <DataTable 
                    header={renderHeader()}
                    value={equipes} 
                    loading={loading}
                    filters={filters} 
                    stripedRows
                    paginator
                    rows={10}
                    className="data-table"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} equipes"
                    rowsPerPageOptions={[10, 25, 50]}
                >
                    <Column field="nome" header="Nome" sortable style={{ minWidth: "200px" }} />
                    <Column 
                        field="tipoServico" 
                        header="Tipo de Serviço" 
                        style={{ minWidth: "200px" }} 
                        body={tipoServicoBodyTemplate}
                    />
                    <Column 
                        field="ativo" 
                        header="Status" 
                        body={statusBodyTemplate}
                        sortable
                        style={{ minWidth: "100px" }}
                    />
                    <Column 
                        field="terceirizada"
                        body={(rowData) => rowData.terceirizada ? "Sim" : "Não"}
                        style={{ width: "100px" }}
                        header="Terceirizada"
                    />
                    <Column 
                        body={actionBodyTemplate} 
                        style={{ width: "100px" }}
                        header="Ações"
                    />
                </DataTable>
            </Card>

            {/* Dialog para adicionar novo equipe */}
            <Dialog 
                visible={displayDialog} 
                style={{ width: "50vw" }} 
                header={equipe.id ? "Editar Equipe" : "Nova Equipe"} 
                modal 
                className="dialog"
                headerClassName="dialog-header"
                contentClassName="dialog-content"
                onHide={() => setDisplayDialog(false)}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                    <div>
                        <label style={{ display: "block", marginBottom: "5px" }}>Nome *</label>
                        <InputText 
                            placeholder="Digite o nome da equipe"
                            value={equipe.nome || ""}
                            onChange={(e) => setEquipe({ ...equipe, nome: e.target.value })}
                            style={{ width: "100%" }}
                        />
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: "5px" }}>Tipo de Serviço *</label>
                        <ListBox
                            multiple 
                            value={equipe.tipoServico ? JSON.parse(equipe.tipoServico) : []} 
                            onChange={(e) => setEquipe({ ...equipe, tipoServico: JSON.stringify(e.value) })} 
                            options={["Piscinas", "Fotovoltaico", "Aquecimento", "Sauna"].map((tipo) => ({ label: tipo, value: tipo }))} 
                            />
                    </div>

                    <div style={{ padding: "10px", backgroundColor: "var(--app-bg-light)", border: "1px solid var(--app-border-color)", borderRadius: "5px" }}>
                        <Checkbox inputId="terceirizada" checked={equipe.terceirizada || false} onChange={(e) => setEquipe({ ...equipe, terceirizada: e.checked })} />
                        <label htmlFor="terceirizada" style={{ marginLeft: "5px" }}>Equipe Terceirizada</label>
                    </div>

                    <div style={{ display: "grid", gap: "10px", gridTemplateColumns: "1fr 1fr", width: "100%" }}> 
                        <div>
                            <label style={{ display: "block", marginBottom: "5px" }}>Chave Pix 1</label>
                            <InputText 
                                placeholder="Digite a chave Pix"
                                value={equipe.chavePix1 || ""}
                                onChange={(e) => setEquipe({ ...equipe, chavePix1: e.target.value })}
                                style={{ width: "100%" }}
                            />
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: "5px" }}>Chave Pix 2</label>
                            <InputText 
                                placeholder="Digite a chave Pix"
                                value={equipe.chavePix2 || ""}
                                onChange={(e) => setEquipe({ ...equipe, chavePix2: e.target.value })}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: "5px" }}>Integrantes *</label>
                        <InputText 
                            placeholder="Exemplo: João, Maria, José"
                            value={equipe.integrantes || ""}
                            onChange={(e) => setEquipe({ ...equipe, integrantes: e.target.value })}
                            style={{ width: "100%" }}
                        />
                    </div>

                    {hasAnyAuthority(['ADMINISTRADOR']) && (
                        <div>
                            <label style={{ display: "block", marginBottom: "5px" }}>Percentual Comissão</label>
                            <InputNumber
                                placeholder="Exemplo: 10%"
                                suffix="%"
                                min={0}
                                max={100}
                                mode="decimal"
                                value={equipe.percentualComissao || ""}
                                onValueChange={(e) => setEquipe({ ...equipe, percentualComissao: e.value })}
                                style={{ width: "100%" }}
                            />
                        </div>
                    )}

                    <div>
                        <label style={{ display: "block", marginBottom: "5px" }}>Email</label>
                        <InputText 
                            placeholder="Exemplo: joao@example.com"
                            value={equipe.email || ""}
                            onChange={(e) => setEquipe({ ...equipe, email: e.target.value })}
                            style={{ width: "100%" }}
                        />
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: "5px" }}>WhatsApp</label>
                        <InputMask
                            mask="+55 (99) 99999-9999" 
                            placeholder="Exemplo: +55 (11) 91234-5678"
                            value={equipe.whatsapp || ""}
                            onChange={(e) => setEquipe({ ...equipe, whatsapp: e.target.value })}
                            style={{ width: "100%" }}
                        />
                    </div>



                    <div>
                        <label style={{ display: "block", marginBottom: "5px" }}>Status</label>
                        <Dropdown 
                            value={equipe.ativo || false} 
                            options={[{label: "ATIVA", value: true}, {label: "INATIVA", value: false}]} 
                            onChange={(e) => setEquipe({ ...equipe, ativo: e.value })} 
                            style={{ width: "100%" }}    
                        />
                            
                        
                    </div>

                    <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                        <Button 
                            label="Cancelar" 
                            icon="pi pi-times"
                            onClick={() => setDisplayDialog(false)}
                            className="button-cancel"
                        />
                        <Button 
                            label="Salvar" 
                            icon="pi pi-save"
                            onClick={save}
                            className="button-save"
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
